import React, { useEffect, useImperativeHandle, useState } from 'react'
import { dietTypes, listData, OnboardPage } from '../../../helpers/onboard-helper'
import { getOnboardingStepPoints } from '../../../helpers/onboarding-points'
import { OnboardSelectionMethods } from '../../../types'
import OnboardPreferenceOptions from './OnboardPreferenceOptions'
import OnboardTitleTypography from './OnboardTitleTypography'
import {
  OnboardPreferencesDisLikeContainer,
  OnboardPreferencesLikeContainer,
} from './styled/OnboardPreferences.styled'

interface OnboardSelectionProps {
  key?: number
  title?: string
  subTitle?: string
  secondSubTitle?: string
  listName: string
  liked?: string[]
  dislike?: string[]
  indexKey: string
  isEditPreference?: boolean
  alignItems?: 'center' | 'right' | 'left'
  page?: OnboardPage // Optional page enum to determine points
  setNextButtonDisable?: (disable: boolean) => void // Optional validation callback
}

const OnboardPreferences = React.forwardRef<
  OnboardSelectionMethods,
  OnboardSelectionProps
>(function OnboardSelection(
  {
    title,
    subTitle,
    secondSubTitle,
    listName,
    liked,
    dislike,
    indexKey,
    isEditPreference = false,
    alignItems = 'center',
    page,
    setNextButtonDisable,
  }: OnboardSelectionProps,
  ref,
): JSX.Element {
  const [itemList, setItemList] = useState<string[]>([])
  const [likedItems, setLikedItems] = useState<string[]>(liked ?? [])
  const [dislikedItems, setDislikedItems] = useState<string[]>(dislike ?? [])

  const firstTestId = `1`
  const secondTestId = `2`

  // Diet types (vegan, carnivore, …) are mutually exclusive within the dietary-preferences step;
  // additive restrictions (gluten-free, keto, …) still stack. Only applies to that step.
  const exclusiveGroup =
    listName === 'dietaryPreferences' ? dietTypes : undefined

  useEffect(() => {
    setItemList(listData[listName] || [])
  }, [listName])

  // Sync initial values from props only when switching steps (listName changes) or entering edit mode
  // Do NOT watch liked/dislike props directly as they create new array references on each render
  // (parent passes `liked ?? []` which creates a new array every time)
  useEffect(() => {
    setLikedItems(liked ?? [])
    setDislikedItems(dislike ?? [])
    // eslint-disable-next-line
  }, [listName, isEditPreference])

  // Validation: require at least 1 selection in likes (or value list for dietary/allergies)
  useEffect(() => {
    if (isEditPreference || !setNextButtonDisable) return
    
    // For steps with only one list (dietary/allergies), check likedItems
    // For steps with likes/dislikes, require at least 1 like
    const hasValidSelection = likedItems.length > 0
    setNextButtonDisable(!hasValidSelection)
  }, [likedItems, isEditPreference, setNextButtonDisable])

  useImperativeHandle(ref, () => ({
    getPayload,
  }))

  const getPayload = (): { likedItems: string[]; dislikedItems: string[] } => ({
    likedItems,
    dislikedItems,
  })

  return (
    <>
      {title && (
        <OnboardTitleTypography
          title={title}
          subTitle="Select all that apply or search to add new"
          isEditPreference={isEditPreference}
          alignItems={alignItems}
          points={page ? getOnboardingStepPoints(page) : undefined}
        />
      )}
      <OnboardPreferencesLikeContainer isProfile={isEditPreference}>
        <OnboardPreferenceOptions
          title={subTitle}
          chipType="green"
          itemList={itemList}
          selectedItems={likedItems}
          setSelectedItems={setLikedItems}
          oppositeItems={dislikedItems}
          searchIndexKey={indexKey}
          isPreferenceEdit={isEditPreference}
          testId={firstTestId}
          alignItems={alignItems}
          exclusiveGroup={exclusiveGroup}
        />
      </OnboardPreferencesLikeContainer>
      {secondSubTitle && secondSubTitle.length > 0 && (
        <OnboardPreferencesDisLikeContainer>
          <OnboardPreferenceOptions
            title={secondSubTitle}
            chipType="red"
            itemList={itemList}
            selectedItems={dislikedItems}
            setSelectedItems={setDislikedItems}
            oppositeItems={likedItems}
            searchIndexKey={indexKey}
            isPreferenceEdit={isEditPreference}
            testId={secondTestId}
            alignItems={alignItems}
          />
        </OnboardPreferencesDisLikeContainer>
      )}
    </>
  )
})

export default OnboardPreferences
